import { useQuery } from '@apollo/client';
import { ErrorLocal, QuizList, Spinner } from 'components';
import { useEventContext, useUserContext } from 'contexts';
import { Quiz } from 'graphql/fragments/types/Quiz';
import { QuizWithEventInfo } from 'graphql/fragments/types/QuizWithEventInfo';
import { getRootQuizzes, getEventQuizzes, getEventQuizzesByCreator } from 'graphql/queries';
import { EventQuizzes, EventQuizzesVariables } from 'graphql/queries/types/EventQuizzes';
import { EventQuizzesByCreator, EventQuizzesByCreatorVariables } from 'graphql/queries/types/EventQuizzesByCreator';
import { RootQuizzes } from 'graphql/queries/types/RootQuizzes';
import { useNavigate, useParams } from 'react-router-dom';
import { NavUtils, NavPages } from 'utils';

interface Props {}


// eslint-disable-next-line @typescript-eslint/no-unused-vars
enum Scenes {
    QuizSelect,
    QuizSetup
}

// TODO - once quiz is selected load QuizSessionSetup component to setup the session parameters

export const QuizSessionQuizSelector = (props: Props) => {
    const navigate = useNavigate();
    const event = useEventContext();
    const user = useUserContext();
    const { eventAlias } = useParams();

    // ROOT - used only by the Super Admin role
    const {
        loading: loadingRootQuizzes,
        error: errorRootQuizzes,
        data: rootQuizzes
        // refetch
    } = useQuery<RootQuizzes>(getRootQuizzes, {
        variables: {
            authorUserId: user.id
        },
        skip: !!event || !user.isSuperAdmin()
    });

    // Event quizzes from creator
    const {
        loading: loadingEventQuizzes,
        error: errorEventQuizzes,
        data: eventCreatorQuizzes
        // refetch
    } = useQuery<EventQuizzesByCreator, EventQuizzesByCreatorVariables>(getEventQuizzesByCreator, {
        variables: {
            authorUserId: user.id,
            eventId: event!.id
        },
        skip: !event || user.isEventAdmin(eventAlias)
    });

    // All event quizzes
    const {
        loading: loadingAllEventQuizzes,
        error: errorAllEventQuizzes,
        data: allEventQuizzes
        // refetch
    } = useQuery<EventQuizzes, EventQuizzesVariables>(getEventQuizzes, {
        variables: { eventId: event!.id },
        skip: !event || !user.isEventAdmin(eventAlias)
    });

    if (loadingRootQuizzes || loadingEventQuizzes || loadingAllEventQuizzes) return <Spinner />;
    if (errorRootQuizzes || errorEventQuizzes || errorAllEventQuizzes) return <ErrorLocal errorMessage="Error getting quizzes :(" />;
    if (!eventCreatorQuizzes && !rootQuizzes && !allEventQuizzes) return <p>No data</p>;

    const onQuizSelect = (quiz: QuizWithEventInfo) => {
        navigate(NavUtils.generateNavPath(NavPages.CreateQuizSession, quiz.id, quiz.event?.alias));
    }

    const enrichQuizListWithEventData = (quizList?: Quiz[]): QuizWithEventInfo[] => {
        if (!quizList) {
            return [];
        }

        if (!event) {
            return quizList.map((q) => ({ ...q, event: null }));
        }
        return quizList.map((q) => ({
            ...q,
            event: {
                id: event!.id,
                alias: event!.alias
            }
        }));
    };

    const quizzes: QuizWithEventInfo[] = [
        ...enrichQuizListWithEventData(rootQuizzes?.quiz_master_quizzes),
        ...enrichQuizListWithEventData(eventCreatorQuizzes?.quiz_master_quizzes),
        ...enrichQuizListWithEventData(allEventQuizzes?.quiz_master_quizzes)
    ];
    return (
        <div className="aq-content">
            <QuizList quizList={quizzes} onCustomQuizSelect={onQuizSelect}/>
        </div>
    );
};
